/**
 * Tweestapsverificatie instellen, schermvullend en stap voor stap. Wie 2FA moet gebruiken en hem
 * niet aan heeft, komt hier terecht en kan niets anders tot hij klaar is (de backend weigert de
 * rest met `TWO_FACTOR_REQUIRED`). Vanaf de pagina Beveiliging komt hier ook wie hem vrijwillig
 * aanzet, of met `?mode=replace` naar een nieuwe telefoon overstapt.
 * Spec `docs/superpowers/specs/2026-10-20-two-factor-design.md`.
 */

import { cn } from '@cloud/shared/ui'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { Check, Copy, ShieldCheck, Smartphone, TriangleAlert } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { LanguageDropdown } from '@/components/language-dropdown'
import { ModeDropdown } from '@/components/mode-dropdown'
import { BackupCodes } from '@/components/two-factor/backup-codes'
import { TwoFactorCodeInput } from '@/components/two-factor/code-input'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { authErrorMessage } from '@/lib/auth-error'
import { useUser } from '@/providers/user-provider'
import { authClient } from '../lib/auth'

export const Route = createFileRoute('/setup-2fa')({
  component: Setup2FAPage,
  validateSearch: (search: Record<string, unknown>): { mode?: 'replace' } =>
    search.mode === 'replace' ? { mode: 'replace' } : {},
})

const STEPS = ['app', 'password', 'scan', 'verify', 'backup'] as const
type Step = (typeof STEPS)[number]

/** Gangbare gratis apps; elke app die TOTP-codes toont werkt. */
const APPS = ['Google Authenticator', 'Microsoft Authenticator', 'Authy', '1Password']

/** Het geheim uit de otpauth-URI, om met de hand in te typen als scannen niet lukt. */
function secretOf(totpURI: string): string {
  try {
    return new URL(totpURI).searchParams.get('secret') ?? ''
  } catch {
    return ''
  }
}

function Setup2FAPage() {
  const t = useTranslations()
  const ts = (k: string, vars?: Record<string, string>) => t(`twoFactor.setup.${k}`, vars)
  const navigate = useNavigate()
  const { mode } = Route.useSearch()
  const replacing = mode === 'replace'
  const { current, loading: userLoading, logout, refresh } = useUser()
  const twoFactor = current?.user?.twoFactor
  const mustSetUp = !!twoFactor?.required && !twoFactor.enabled

  const [step, setStep] = useState<Step>('app')
  const [password, setPassword] = useState('')
  const [totpURI, setTotpURI] = useState<string | null>(null)
  const [backupCodes, setBackupCodes] = useState<string[]>([])
  const [code, setCode] = useState('')
  const [saved, setSaved] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  // Zonder sessie is er niets in te stellen; wie hem al aan heeft en niet overstapt is klaar.
  useEffect(() => {
    if (userLoading) return
    if (!current?.user) void navigate({ to: '/login', replace: true })
    else if (twoFactor?.enabled && !replacing && step === 'app') {
      void navigate({ to: '/account/security', replace: true })
    }
  }, [userLoading, current, twoFactor?.enabled, replacing, step, navigate])

  const leave = () => void navigate({ to: mustSetUp ? '/' : '/account/security' })

  const enable = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(null)
    try {
      const result = await authClient.twoFactor.enable({ password })
      if (result.error) {
        setError(authErrorMessage(result.error, t, 'errors.INVALID_PASSWORD'))
        return
      }
      const data = result.data as { totpURI?: string; backupCodes?: string[] } | null
      setTotpURI(data?.totpURI ?? null)
      setBackupCodes(data?.backupCodes ?? [])
      setPassword('')
      setStep('scan')
    } finally {
      setBusy(false)
    }
  }

  const verify = async (value: string) => {
    if (value.length !== 6) return
    setBusy(true)
    setError(null)
    try {
      const result = await authClient.twoFactor.verifyTotp({ code: value })
      if (result.error) {
        setError(ts('verify.wrong'))
        setCode('')
        return
      }
      setStep('backup')
    } finally {
      setBusy(false)
    }
  }

  const finish = async () => {
    await refresh()
    toast.success(ts('done'))
    void navigate({ to: mustSetUp ? '/' : '/account/security', replace: true })
  }

  const index = STEPS.indexOf(step)

  return (
    <div className="flex min-h-screen w-full flex-col bg-muted">
      <header className="flex items-center justify-between gap-4 border-b bg-background px-6 py-3">
        <div className="flex min-w-0 items-center gap-2 font-semibold">
          <ShieldCheck className="size-5 shrink-0 text-primary" />
          <span className="truncate">{current?.org.name}</span>
        </div>
        <div className="flex items-center gap-1">
          <LanguageDropdown />
          <ModeDropdown />
          {mustSetUp ? (
            <Button variant="ghost" size="sm" onClick={() => void logout()}>
              {ts('signOut')}
            </Button>
          ) : (
            <Button variant="ghost" size="sm" onClick={leave}>
              {ts('cancel')}
            </Button>
          )}
        </div>
      </header>

      <main className="flex flex-1 justify-center px-4 py-10">
        <div className="w-full max-w-3xl space-y-8">
          <div className="space-y-3">
            <h1 className="text-3xl font-semibold">
              {replacing ? ts('titleReplace') : ts('titleSetup')}
            </h1>
            <p className="text-muted-foreground">{ts('intro')}</p>
            {mustSetUp && twoFactor?.reason && (
              <Alert>
                <ShieldCheck className="size-4" />
                <AlertDescription>{t(`twoFactor.reason.${twoFactor.reason}`)}</AlertDescription>
              </Alert>
            )}
            {replacing && (
              <Alert variant="destructive">
                <TriangleAlert className="size-4" />
                <AlertDescription>{ts('replaceWarning')}</AlertDescription>
              </Alert>
            )}
          </div>

          <ol className="grid grid-cols-5 gap-2">
            {STEPS.map((s, i) => (
              <li key={s} className="space-y-2">
                <div
                  className={cn('h-1.5 rounded-full', i <= index ? 'bg-primary' : 'bg-border')}
                />
                <div
                  className={cn(
                    'flex items-center gap-1.5 text-xs',
                    i === index ? 'font-medium text-foreground' : 'text-muted-foreground',
                  )}
                >
                  {i < index ? (
                    <Check className="size-3.5 shrink-0 text-primary" />
                  ) : (
                    <span className="tabular-nums">{i + 1}.</span>
                  )}
                  <span className="hidden truncate sm:inline">{ts(`steps.${s}`)}</span>
                </div>
              </li>
            ))}
          </ol>

          <Card>
            <CardContent className="space-y-6 p-8">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">
                  {ts('stepOf', { n: String(index + 1), total: String(STEPS.length) })}
                </p>
                <h2 className="text-xl font-semibold">{ts(`${step}.heading`)}</h2>
                <p className="text-muted-foreground">{ts(`${step}.body`)}</p>
              </div>

              {step === 'app' && (
                <>
                  <ul className="grid gap-2 sm:grid-cols-2">
                    {APPS.map((app) => (
                      <li key={app} className="flex items-center gap-3 rounded-lg border p-3">
                        <Smartphone className="size-5 text-muted-foreground" />
                        <span className="font-medium">{app}</span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-sm text-muted-foreground">{ts('app.note')}</p>
                  <div className="flex justify-end">
                    <Button onClick={() => setStep('password')}>{ts('continue')}</Button>
                  </div>
                </>
              )}

              {step === 'password' && (
                <form onSubmit={enable} className="space-y-4">
                  <div className="max-w-sm space-y-2">
                    <Label htmlFor="setup-password">{ts('password.label')}</Label>
                    <Input
                      id="setup-password"
                      type="password"
                      autoComplete="current-password"
                      autoFocus
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </div>
                  {error && <p className="text-sm text-destructive">{error}</p>}
                  <div className="flex justify-between gap-2">
                    <Button type="button" variant="outline" onClick={() => setStep('app')}>
                      {ts('back')}
                    </Button>
                    <Button type="submit" disabled={busy || !password}>
                      {ts('continue')}
                    </Button>
                  </div>
                </form>
              )}

              {step === 'scan' && totpURI && (
                <>
                  <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-start">
                    <div className="shrink-0 rounded-lg border p-3">
                      <QRCodeSVG value={totpURI} size={192} />
                    </div>
                    <div className="space-y-2">
                      <p className="text-sm text-muted-foreground">{ts('scan.manual')}</p>
                      <code className="block break-all rounded-md bg-muted px-3 py-2 font-mono text-sm">
                        {secretOf(totpURI)}
                      </code>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          void navigator.clipboard.writeText(secretOf(totpURI)).then(() => {
                            setCopied(true)
                            setTimeout(() => setCopied(false), 2000)
                          })
                        }
                      >
                        {copied ? (
                          <Check className="me-2 size-4" />
                        ) : (
                          <Copy className="me-2 size-4" />
                        )}
                        {copied ? ts('scan.copied') : ts('scan.copy')}
                      </Button>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={() => setStep('verify')}>{ts('continue')}</Button>
                  </div>
                </>
              )}

              {step === 'verify' && (
                <>
                  <div className="space-y-3">
                    <TwoFactorCodeInput
                      value={code}
                      onChange={setCode}
                      onComplete={(value) => void verify(value)}
                      disabled={busy}
                      autoFocus
                    />
                    {error && <p className="text-sm text-destructive">{error}</p>}
                  </div>
                  <div className="flex justify-between gap-2">
                    <Button type="button" variant="outline" onClick={() => setStep('scan')}>
                      {ts('back')}
                    </Button>
                    <Button onClick={() => void verify(code)} disabled={busy || code.length !== 6}>
                      {ts('continue')}
                    </Button>
                  </div>
                </>
              )}

              {step === 'backup' && (
                <>
                  <BackupCodes codes={backupCodes} />
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="backup-saved"
                      checked={saved}
                      onCheckedChange={(value) => setSaved(value === true)}
                    />
                    <Label htmlFor="backup-saved">{ts('backup.saved')}</Label>
                  </div>
                  <div className="flex justify-end">
                    <Button onClick={() => void finish()} disabled={!saved}>
                      {ts('finish')}
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  )
}
